import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { SectionLabel } from "../components/site/SectionLabel";
import { CTASection } from "../components/site/CTASection";
import { IMAGES } from "../data/images";

const STORY = [
  { h: "Engineering Foundation", p: "A chemical engineering background with hands-on polyurethane manufacturing, R&D and formulation experience — the technical grounding behind MATRIVON's industrial perspective." },
  { h: "International Commercial Leadership", p: "Regional sales leadership and market development across Turkey, the Middle East, North Africa and CIS markets, developing customers, distributors and new business." },
  { h: "General Management", p: "Operations, budgets, strategy and commercial execution — understanding a business as a whole, not only its sales function." },
  { h: "Strategic Projects", p: "Feasibility studies, acquisition-related work and business opportunity evaluation across industrial and specialty-chemical markets." },
  { h: "Independent Perspective", p: "These experiences combine into a single advantage: the ability to connect technical reality, market knowledge and commercial judgement when advising businesses today." },
];

export default function About() {
  return (
    <div>
      <PageHero
        variant="dark"
        label="About"
        title="A Different Type of Advisory Platform"
        intro="MATRIVON is built around a simple principle: better business decisions come from connecting commercial opportunity with technical and market reality."
        crumbs={[{ label: "About" }]}
      />

      <section className="border-b border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <SectionLabel>Why MATRIVON exists</SectionLabel>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-brand-ink">
                The perspective behind MATRIVON
              </h2>
            </div>
            <div className="space-y-5 text-[17px] leading-relaxed text-brand-ink/80">
              <p>
                Business decisions in industrial markets rarely fit neatly into a strategy
                presentation. MATRIVON exists to work at the intersection of commercial strategy,
                international markets and industrial expertise — where most real decisions actually
                live.
              </p>
              <p>
                The platform combines technical understanding, commercial experience, international
                market exposure and independent analysis. It is deliberately not a chemical
                consultancy, nor a generic management consultancy. Its differentiation is the ability
                to connect markets, commercial decisions and technical reality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="border-b border-brand-line">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="mb-6 overflow-hidden border border-brand-line">
                <img src={IMAGES.founder} alt="Polyurethane and silicone industrial materials" className="aspect-[4/3] w-full object-cover" />
              </div>
              <SectionLabel>Founder</SectionLabel>
              <h2 className="font-heading text-2xl font-bold text-brand-ink">Sönmez Işık</h2>
              <p className="text-sm font-semibold text-brand-blue">Founder & Principal Advisor</p>
              <p className="mt-2 text-sm text-brand-muted">Chemical Engineer & International Commercial Advisor · 25+ years</p>
            </div>

            <div className="space-y-px border border-brand-line bg-brand-line">
              {STORY.map((s, i) => (
                <div key={i} className="grid gap-4 bg-white p-7 sm:grid-cols-[auto_1fr]">
                  <div className="font-mono text-2xl font-bold text-brand-blue">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-brand-ink">{s.h}</h3>
                    <p className="mt-1.5 text-[15px] leading-relaxed text-brand-muted">{s.p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection
        label="Work with MATRIVON"
        title="Better-informed decisions with a clearer understanding of opportunity and risk."
        primary={{ to: "/contact", label: "Book a Conversation" }}
        secondary={{ to: "/advisory", label: "Explore Advisory" }}
      />
    </div>
  );
}
